'use client';
import React, { useState } from 'react';
import { Zap, X, ArrowRight } from 'lucide-react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface BoostPopupProps {
  postId: string | null;
  postSlug?: string | null;
  onClose: () => void;
}

export default function BoostPopup({ postId, postSlug, onClose }: BoostPopupProps) {
  const router = useRouter();
  const [skipped, setSkipped] = useState(false);

  const handleBoost = () => {
    onClose();
    router.push('/boost-payment-screen');
  };

  const handleDoubleVotes = () => {
    onClose();
    router.push('/boost-payment-screen');
  };

  const handleSkip = () => {
    setSkipped(true);
  };

  if (skipped) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
        <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={18} />
          </button>

          <div className="text-center py-2">
            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
              <Zap size={22} className="text-green-500" />
            </div>
            <h2 className="text-base font-bold text-foreground mb-1">Your post is live!</h2>
            <p className="text-sm text-muted-foreground mb-5">What would you like to do next?</p>

            <div className="flex flex-col gap-3">
              {postSlug && (
                <Link
                  href={`/p/${postSlug}`}
                  onClick={onClose}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold py-2.5 rounded-xl hover:bg-indigo-600 transition-all duration-150 active:scale-95 text-sm"
                >
                  View your post
                  <ArrowRight size={14} />
                </Link>
              )}
              <button
                onClick={onClose}
                className="w-full flex items-center justify-center gap-2 bg-muted text-foreground font-semibold py-2.5 rounded-xl hover:bg-muted/80 transition-all duration-150 active:scale-95 text-sm"
              >
                Back to feed
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Zap size={20} className="text-yellow-500" />
          <h2 className="text-lg font-bold text-foreground">Your post is live!</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">Want more people to see it?</p>

        <div className="flex flex-col gap-3">
          <button
            onClick={handleBoost}
            className="w-full bg-primary text-primary-foreground font-semibold py-2.5 rounded-xl hover:bg-indigo-600 transition-all duration-150 active:scale-95 text-sm"
          >
            ⚡ Push to top
          </button>
          <button
            onClick={handleDoubleVotes}
            className="w-full bg-yellow-50 text-yellow-700 border border-yellow-200 font-semibold py-2.5 rounded-xl hover:bg-yellow-100 transition-all duration-150 active:scale-95 text-sm"
          >
            ×2 Double your votes
          </button>
          <button
            onClick={handleSkip}
            className="w-full text-muted-foreground text-sm py-2 hover:text-foreground transition-colors"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}
